/* Theme-scoped integration for managed Hello Retail HTML, including re-renders.
 * No provider settings are modified: the live theme keeps its own presentation.
 */
(function () {
  'use strict';
  var cardSelector = '.productItem';
  var pending = new Set();
  var scheduled = false;
  var nameFitPending = new Set();
  var nameFitScheduled = false;
  var observedNames = new WeakSet();
  var nameCanvas;
  var nameResizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(function (entries) {
    entries.forEach(function (entry) { var title = entry.target.querySelector('.pTitHide'); if (title) queueNameFit(title); });
  }) : null;

  // Whole-word display alternatives keep the live font size. The full name is
  // retained on the link and product page, including for new catalog entries.
  function nameOptions(name, fullName) {
    var options = [name];
    function add(value) {
      value = value.replace(/\s+/g, ' ').trim();
      if (value && options.indexOf(value) === -1) options.push(value);
      return value;
    }
    var compact = add(name.replace(/\bT-shirt\b/gi, 'Tee').replace(/\bSweatshirt\b/gi, 'Sweat').replace(/\bFull[- ]Zip\b/gi, 'Zip').replace(/\bSneakers\b/gi, 'Sneaker'));
    compact = add(compact.replace(/\b(?:Regular|Signature|Cotton|Bamboo|Organic|Stretch|Premium|Classic|Logo|Leather|Suede|Recycled|Hooded|Performance|Training)\b/gi, ''));
    var pack = name.match(/\b\d+[- ]pack\b/i);
    if (pack) {
      var type = compact.match(/\b(?:Tee|Trunks|Boxers|Socks|Briefs|Thong|Bralette)\b/i);
      if (type) add(pack[0] + ' ' + type[0]);
      add(pack[0]);
      add(pack[0].match(/\d+/)[0] + ' stk.');
    } else {
      var model = add(compact.replace(/\b(?:Quilted|Fleece|Zip|Half[- ]Zip|L\/S|S\/S|Knit|Sweater|Sweat|Sweatpants|Hoodie|Jacket|Parka|Puffer|Tee|Shirt|Polo|Sneaker|Trainer|Shorts|Pants|Jeans|Dress|Skirt|Belt|Cap|Bra|Bikini|Top)\b/gi, ''));
      // Prefer the identifying model, then complete shorter words if necessary.
      (model || compact).split(/\s+/).forEach(add);
      compact.split(/\s+/).forEach(add);
      // Established garment terms are the last compact alternative on very
      // narrow cards, rather than broken model names or unreadably small type.
      var description = name + ' ' + (fullName || '');
      [
        [/\b(?:Hoodie|Sweatshirt|Sweat)\b/i, 'Sweat'],
        [/\b(?:Pullover|Sweater|Knit|Turtleneck)\b/i, 'Knit'],
        [/\b(?:Sneakers?|Trainers?|Slippers|Espadrilles)\b/i, 'Sko'],
        [/\b(?:Sweatpants|Joggers|Leggings)\b/i, 'Pants'],
        [/\bOvershirt\b/i, 'Shirt'],
        [/\b(?:Jacket|Parka|Puffer|Windbreaker|Anorak)\b/i, 'Jakke'],
        [/\bBralette\b/i, 'BH'],
        [/\bSwimsuit\b/i, 'Swim'],
        [/\b(?:Tanktop|Midlayer)\b/i, 'Top'],
        [/\bBackpack\b/i, 'Taske'],
        [/\bTracksuit\b/i, 'Sæt'],
        [/\bHalf[- ]Zip\b/i, 'Zip'],
        [/\b(?:T-shirt|Tee)\b/i, 'Tee']
      ].forEach(function (alternative) { if (alternative[0].test(description)) add(alternative[1]); });
    }
    return options;
  }

  function fitName(title) {
    if (!title.isConnected) return;
    var card = title.closest('.productItem');
    var meta = card.querySelector('.p-meta');
    var left = title.closest('.lefty');
    if (!meta || !left || !meta.clientWidth) return;
    var reference = card.querySelector('.rrp-collection-reference:not([hidden])') || card.querySelector('.righty s:not([hidden])');
    var available = left.clientWidth;
    if (reference) {
      // A flex reference can be wider than the original price column. Measure
      // its text as a unit, not its overflowing wrapper's nominal width.
      var range = document.createRange();
      range.selectNodeContents(reference);
      var refBox = range.getBoundingClientRect();
      if (refBox.width) available = Math.min(available, refBox.left - title.getBoundingClientRect().left - 5);
    }
    if (available <= 0) return;
    var widthValue = Math.floor(available) + 'px';
    if (title.style.getPropertyValue('--luxivo-name-width') !== widthValue) title.style.setProperty('--luxivo-name-width', widthValue);
    nameCanvas = nameCanvas || document.createElement('canvas');
    var context = nameCanvas.getContext('2d');
    if (!context) return;
    var style = getComputedStyle(title);
    context.font = style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily;
    var options = nameOptions(title.dataset.cardShortName || title.dataset.cardFullName || title.textContent, title.dataset.cardFullName);
    var measure = function (text) { return context.measureText(style.textTransform === 'uppercase' ? text.toUpperCase() : text).width + (parseFloat(style.letterSpacing) || 0) * text.length; };
    var chosen = options.find(function (text) { return measure(text) <= available - 1; }) || options.reduce(function (a, b) { return measure(a) <= measure(b) ? a : b; });
    title.dataset.cardDisplayName = chosen;
    textIfChanged(title, chosen);
  }

  function queueNameFit(title) {
    nameFitPending.add(title);
    if (nameResizeObserver && !observedNames.has(title)) {
      observedNames.add(title);
      nameResizeObserver.observe(title.closest('.p-meta'));
    }
    if (nameFitScheduled) return;
    nameFitScheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      nameFitScheduled = false;
      nameFitPending.forEach(fitName);
      nameFitPending.clear();
    });
  }

  function refitNames() {
    document.querySelectorAll('.productItem .pTitHide').forEach(queueNameFit);
  }

  function releaseNames(node) {
    if (!nameResizeObserver || node.nodeType !== 1 || node.isConnected) return;
    var titles = Array.from(node.querySelectorAll('.pTitHide'));
    if (node.matches('.pTitHide')) titles.push(node);
    titles.forEach(function (title) {
      nameResizeObserver.unobserve(title.closest('.p-meta'));
      observedNames.delete(title);
      nameFitPending.delete(title);
    });
  }

  function parseMoney(text) {
    var value = String(text || '').replace(/[^\d,.-]/g, '').replace(/[.,]+$/, '');
    if (!value) return NaN;
    if (value.indexOf(',') !== -1) {
      value = value.replace(/\./g, '').replace(',', '.');
    } else if (/^\d{1,3}(\.\d{3})+$/.test(value)) {
      value = value.replace(/\./g, '');
    }
    return Math.round(Number(value) * 100);
  }

  function directPriceText(element) {
    return Array.from(element.childNodes).filter(function (node) {
      return node.nodeType === 3;
    }).map(function (node) { return node.textContent; }).join(' ').trim();
  }

  function textIfChanged(element, value) {
    if (element.textContent !== value) element.textContent = value;
  }

  function updateCardName(card) {
    var title = card.querySelector('.pTitHide');
    var link = title && title.closest('a[href]');
    if (!link) return;
    var match;
    try { match = new URL(link.href, document.baseURI).pathname.match(/\/products\/([^/]+)/); }
    catch (error) { return; }
    if (!match) return;
    var handle = decodeURIComponent(match[1]);
    var names = window.LuxivoCardNames || {};
    var shortName = Object.prototype.hasOwnProperty.call(names, handle) ? names[handle] : null;
    var current = title.textContent.trim();
    // A provider may reuse nodes when sorting or replacing recommendations.
    // Keep the new provider title if either the product or its text changes.
    if (title.dataset.cardHandle !== handle || current !== title.dataset.cardDisplayName) {
      title.dataset.cardFullName = current;
    }
    var fullName = title.dataset.cardFullName || current;
    var displayName = shortName || fullName;
    title.dataset.cardHandle = handle;
    if (title.dataset.cardShortName !== displayName || current !== title.dataset.cardDisplayName) {
      title.dataset.cardShortName = displayName;
      title.dataset.cardDisplayName = displayName;
      textIfChanged(title, displayName);
    }
    var brand = card.querySelector('.fat-vendor');
    var accessibleName = ((brand ? brand.textContent.trim() + ' ' : '') + fullName).trim();
    if (link.title !== accessibleName) link.title = accessibleName;
    if (link.getAttribute('aria-label') !== accessibleName) link.setAttribute('aria-label', accessibleName);
    card.classList.add('luxivo-short-name');
    queueNameFit(title);
  }

  function updateCard(card) {
    updateCardName(card);
    var prices = card.querySelector('.righty');
    if (!prices) return;
    var compare = prices.querySelector('s');
    // Native Liquid cards already use a matching variant and need no adaptation.
    if (!card.querySelector('.aw-infoBox') && !card.classList.contains('aw-item')) return;
    var selling = prices.querySelector('.rrp-selling-price');
    var price = parseMoney(selling ? selling.textContent : directPriceText(prices));
    var rrp = compare ? parseMoney(compare.textContent) : NaN;
    var valid = Number.isFinite(price) && price >= 0 && Number.isFinite(rrp) && rrp > price;
    var badge = card.querySelector('.aw-saleBadges');

    if (!selling && Number.isFinite(price)) {
      // Wrap only the provider's price text; preserve links, handlers and other nodes.
      selling = document.createElement('span');
      selling.className = 'rrp-selling-price';
      selling.textContent = directPriceText(prices);
      Array.from(prices.childNodes).forEach(function (node) {
        if (node.nodeType === 3) node.remove();
      });
      prices.insertBefore(selling, prices.firstChild);
    }

    if (compare) {
      compare.classList.add('rrp-card-reference');
      compare.title = 'Vejl. pris';
      compare.setAttribute('aria-label', 'Vejl. pris ' + compare.textContent.trim());
      compare.hidden = !valid;
      // Collection cards label the RRP explicitly. Other surfaces
      // retain the approved presentation, including product-page recommendations.
      if (document.body.classList.contains('template-collection')) {
        var reference = compare.closest('.rrp-collection-reference');
        if (!reference) {
          reference = document.createElement('span');
          reference.className = 'rrp-collection-reference';
          var referenceLabel = document.createElement('span');
          referenceLabel.className = 'rrp-label';
          referenceLabel.textContent = 'Vejl. pris';
          compare.before(reference);
          reference.append(referenceLabel, document.createTextNode(' '), compare);
        }
        reference.hidden = !valid;
      }
    }

    if (valid) {
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'aw-saleBadges';
        (card.querySelector('.padded-inner') || card).prepend(badge);
      }
      badge.classList.add('rrp-card-badge', 'rrp-badge');
      var percent = badge.querySelector('.rrp-percent');
      if (!percent) {
        percent = document.createElement('span');
        percent.className = 'aw-sale rrp-percent';
        badge.replaceChildren(percent);
      }
      textIfChanged(percent, '-' + Math.floor((rrp - price) * 100 / rrp) + '%');
      badge.hidden = false;
    } else if (badge) {
      badge.classList.add('rrp-badge');
      badge.hidden = true;
    }
  }

  function collect(node) {
    if (node.nodeType === 3) node = node.parentElement;
    if (!node || node.nodeType !== 1) return;
    var card = node.closest(cardSelector);
    if (card) pending.add(card);
    node.querySelectorAll(cardSelector).forEach(function (item) { pending.add(item); });
  }

  function flush() {
    scheduled = false;
    var cards = Array.from(pending);
    pending.clear();
    cards.forEach(function (card) { if (card.isConnected) updateCard(card); });
  }

  function start() {
    document.querySelectorAll(cardSelector).forEach(updateCard);
    window.addEventListener('resize', refitNames);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refitNames);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        collect(mutation.target);
        mutation.addedNodes.forEach(collect);
        mutation.removedNodes.forEach(releaseNames);
      });
      if (pending.size && !scheduled) {
        scheduled = true;
        queueMicrotask(flush);
      }
    }).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['href'] });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
